import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeftIcon, LockIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getApiEndpoint } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const email = useMemo(() => searchParams.get("email") || "", [searchParams]);

  const isFormDisabled = !token || !email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast({
        title: t.auth.invalidPassword,
        description: t.auth.passwordMinLength,
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t.auth.passwordsDontMatch,
        description: t.auth.confirmPasswordCorrect,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(getApiEndpoint("/api/auth/reset-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          token,
          new_password: password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: t.auth.passwordReset,
          description: data.message || t.auth.passwordResetDesc,
        });
        setTimeout(() => {
          navigate("/login");
        }, 1200);
      } else {
        toast({
          title: t.auth.resetError,
          description: data.error || t.auth.resetErrorDesc,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: t.auth.resetError,
        description: t.auth.connectionError,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/login" className="inline-flex items-center text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            {t.auth.backToLogin}
          </Link>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">TEC-IT ITSM</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t.auth.resetPasswordTitle}</p>
          </div>
        </div>

        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle>{t.auth.newPassword}</CardTitle>
            <CardDescription>
              {t.auth.newPasswordDesc}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isFormDisabled && (
              <div className="mb-4 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
                {t.auth.invalidLink}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t.auth.newPassword}</Label>
                <div className="relative">
                  <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    disabled={isFormDisabled}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t.auth.confirmPassword}</Label>
                <div className="relative">
                  <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    disabled={isFormDisabled}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading || isFormDisabled}>
                {isLoading ? t.auth.saving : t.auth.savePassword}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
